import React, { useEffect, useState, useCallback } from 'react';
import { apiService } from '../services/api';
import { useApi, useMutation } from '../hooks/useApi';
import logger from '../utils/logger';
import env from '../config/env';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import StokuModal from './StokuModal';
import DetajeModal from './DetajeModal';
import EditModal from './EditModal';
import ProductCard from './ProductCard';
import imageCompression from 'browser-image-compression';
import {
  Boxes,
  PackagePlus,
  Search,
  Filter,
  CheckCircle,
  XCircle,
  ImagePlus 
} from 'lucide-react';
import { ArrowLeft, ArrowRight } from 'lucide-react';


function Produkte() {
  const { settings } = useSettings();
  const [produkte, setProdukte] = useState([]);
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const [previewPhotos, setPreviewPhotos] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [kategoriaFilter, setKategoriaFilter] = useState('');
  const [selectedProdukt, setSelectedProdukt] = useState(null);
  const [modalType, setModalType] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [form, setForm] = useState({
    emri: '', emri_mk: '', pershkrimi: '', pershkrimi_mk: '',
    kodi: '', cmimi_blerjes: '', cmimi_shitjes: '',
    zbritje_perqindje: '', kategoria_id: '', furnizues_id: '', eshte_aktiv: true, 
    foto: [{ file: File, ngjyra_id: '' }],
  });

  const perFaqe = 8;

  // Fetch data me useApi hooks
  const { data: produktetData, loading, execute: refetchProdukte } = useApi(
    () => apiService.get('/produkte'),
    [],
    { immediate: true, showNotifications: false }
  );

  const { data: kategoriteData } = useApi(
    () => apiService.get('/kategorite'),
    [],
    { immediate: true, showNotifications: false }
  );

  const { data: furnizuesitData } = useApi(
    () => apiService.get('/furnizuesit'),
    [],
    { immediate: true, showNotifications: false }
  );

  const { data: ngjyratData } = useApi(
    () => apiService.get('/ngjyrat'),
    [],
    { immediate: true, showNotifications: false }
  );

  // Update state kur data ndryshon
  useEffect(() => {
    if (produktetData) {
      const data = Array.isArray(produktetData) ? produktetData : (produktetData?.data || []);
      setProdukte(data);
    }
  }, [produktetData]);

  const kategorite = Array.isArray(kategoriteData) ? kategoriteData : (kategoriteData?.data || []);
  const furnizuesit = Array.isArray(furnizuesitData) ? furnizuesitData : (furnizuesitData?.data || []);
  const ngjyrat = Array.isArray(ngjyratData) ? ngjyratData : (ngjyratData?.data || []);

  const fetchProdukte = useCallback(() => {
    refetchProdukte();
  }, [refetchProdukte]);

  useEffect(() => { if (message) setTimeout(() => setMessage(''), 4000); }, [message]);

  // Mutations
  const { mutate: createProdukt, loading: creatingProdukt } = useMutation(
    async (data) => {
      return await apiService.upload('/produkte', data);
    },
    {
      successMessage: 'Produkti u shtua me sukses!',
      onSuccess: () => {
        setForm({
          emri: '', emri_mk: '', pershkrimi: '', pershkrimi_mk: '',
          kodi: '', cmimi_blerjes: '', cmimi_shitjes: '',
          zbritje_perqindje: '', kategoria_id: '', furnizues_id: '', eshte_aktiv: true,
          foto: []
        });
        setPreviewPhotos([]);
        fetchProdukte();
      },
      onError: (err) => {
        setIsError(true);
        // Format validation errors for display
        if (err.errors && typeof err.errors === 'object') {
          const errorMessages = [];
          Object.keys(err.errors).forEach(key => {
            const fieldErrors = Array.isArray(err.errors[key]) 
              ? err.errors[key] 
              : [err.errors[key]];
            errorMessages.push(...fieldErrors);
          });
          
          if (errorMessages.length > 0) {
            setMessage(errorMessages.join(', '));
          } else {
            setMessage(err.message || 'Një gabim ka ndodhur');
          }
        } else {
          setMessage(err.message || 'Një gabim ka ndodhur');
        }
        logger.error('Validation errors:', err.errors || err);
      }
    }
  );

  const { mutate: updateProdukt, loading: updatingProdukt } = useMutation(
    async ({ id, data }) => {
      return await apiService.upload(`/produkte/${id}?_method=PUT`, data);
    },
    {
      successMessage: 'Produkti u përditësua me sukses!',
      onSuccess: () => {
        setModalType(null);
        setSelectedProdukt(null);
        fetchProdukte();
      },
      onError: (err) => {
        setIsError(true);
        // Format validation errors for display
        if (err.errors && typeof err.errors === 'object') {
          const errorMessages = [];
          Object.keys(err.errors).forEach(key => {
            const fieldErrors = Array.isArray(err.errors[key]) 
              ? err.errors[key] 
              : [err.errors[key]];
            errorMessages.push(...fieldErrors);
          });
          
          if (errorMessages.length > 0) {
            setMessage(errorMessages.join(', '));
          } else {
            setMessage(err.message || 'Një gabim ka ndodhur');
          }
        } else {
          setMessage(err.message || 'Një gabim ka ndodhur');
        }
        logger.error('Gabim gjatë përditësimit:', err);
      }
    }
  );

  const { mutate: deleteProdukt, loading: deletingProdukt } = useMutation(
    async (produktId) => {
      return await apiService.put(`/produkte/${produktId}/aktiv`, { eshte_aktiv: 2 });
    },
    {
      successMessage: 'Produkti u fshi me sukses!',
      onSuccess: () => {
        fetchProdukte();
      }
    }
  );

  const handleSubmit = async (e) => {
    e.preventDefault(); 
    setMessage(''); 
    setIsError(false);
    
    // Client-side validation
    const validationErrors = [];
    if (!form.emri || form.emri.trim() === '') {
      validationErrors.push('Emri i produktit është i detyrueshëm');
    }
    if (!form.kodi || form.kodi.trim() === '') {
      validationErrors.push('Kodi i produktit është i detyrueshëm');
    }
    if (!form.cmimi_blerjes || form.cmimi_blerjes === '') {
      validationErrors.push('Çmimi i blerjes është i detyrueshëm');
    }
    if (!form.cmimi_shitjes || form.cmimi_shitjes === '') {
      validationErrors.push('Çmimi i shitjes është i detyrueshëm');
    }
    if (!form.kategoria_id || form.kategoria_id === '') {
      validationErrors.push('Kategoria është e detyrueshme');
    }
    
    if (validationErrors.length > 0) {
      setIsError(true);
      setMessage(validationErrors.join(', '));
      return;
    }
    
    const data = new FormData();

   // Fushat përveç fotove
  for (const key in form) {
    if (key !== "foto") {
      // Konverto eshte_aktiv në 1 ose 0 për boolean
      if (key === 'eshte_aktiv') {
        data.append(key, form[key] ? '1' : '0');
      } else {
        const value = form[key];
        // Dërgo të gjitha fushat (required fields janë tashmë të validuara)
        // Për fushat opcionale, dërgo edhe nëse janë empty (backend do të trajtojë)
        if (value !== null && value !== undefined) {
          data.append(key, value);
        }
      }
    }
  }

  // Fotot dhe ngjyrat përkatëse
  const fotoValid = form.foto.filter(f => f.file instanceof File);

  if (fotoValid.length > 0) {
    fotoValid.forEach(({ file, ngjyra_id }, index) => {
      data.append(`foto[${index}]`, file);
      data.append(`ngjyra_foto[${index}]`, ngjyra_id ?? '');
    });
  }

  createProdukt(data);
  };

  const handleEdit = (p) => {
    setSelectedProdukt(p);
    setModalType('edit');
  };

//   const handleUpdateProdukt = async (form, previewReja, fotoEkzistuese) => {
//     try {
//       const data = new FormData();
//       for (const key in form) {
//   if (key === 'eshte_aktiv') {
//     data.append('eshte_aktiv', form.eshte_aktiv ? 1 : 0);
//   } else if (key !== 'foto') {
//     data.append(key, form[key]);
//   }
// }

//       previewReja.forEach((item) => {
//         data.append('foto[]', item.file);
//       });
//       const fotoTeMbajtura = fotoEkzistuese.map((f) => f.id);
//       data.append('foto_ekzistuese', JSON.stringify(fotoTeMbajtura));
//       await axios.post(`${process.env.REACT_APP_API_URL}/produkte/${form.id}?_method=PUT`, data, {
//         headers: {
//           Authorization: `Bearer ${token}`,
//           'Content-Type': 'multipart/form-data',
//         },
//       });
//       setIsError(false);
//       setMessage('Produkti u përditësua me sukses!');
      
//       setModalType(null);
//       setSelectedProdukt(null);
//       fetchProdukte();
//     } catch (error) {
//       console.error('Gabim gjatë përditësimit të produktit', error);
//       setMessage('Gabim gjatë përditësimit.');
//       setIsError(true);
//     }
//   };

  const handleUpdateProdukt = async (form, previewReja, fotoEkzistuese) => {
    // Client-side validation before submission
    const validationErrors = [];
    if (!form.emri || String(form.emri).trim() === '') {
      validationErrors.push('Emri i produktit është i detyrueshëm');
    }
    if (!form.kodi || String(form.kodi).trim() === '') {
      validationErrors.push('Kodi i produktit është i detyrueshëm');
    }
    if (form.cmimi_blerjes === null || form.cmimi_blerjes === undefined || form.cmimi_blerjes === '' || (typeof form.cmimi_blerjes === 'string' && form.cmimi_blerjes.trim() === '')) {
      validationErrors.push('Çmimi i blerjes është i detyrueshëm');
    }
    if (form.cmimi_shitjes === null || form.cmimi_shitjes === undefined || form.cmimi_shitjes === '' || (typeof form.cmimi_shitjes === 'string' && form.cmimi_shitjes.trim() === '')) {
      validationErrors.push('Çmimi i shitjes është i detyrueshëm');
    }
    if (!form.kategoria_id || String(form.kategoria_id).trim() === '') {
      validationErrors.push('Kategoria është e detyrueshme');
    }
    
    if (validationErrors.length > 0) {
      setIsError(true);
      setMessage(validationErrors.join(', '));
      return;
    }
    
    const data = new FormData();

    // Helper function to safely convert to string
    const safeString = (val) => {
      if (val === null || val === undefined) return '';
      if (typeof val === 'string') return val.trim();
      return String(val);
    };

    // Helper function for numeric values - ensure we always send a valid number string
    const safeNumeric = (val) => {
      if (val === null || val === undefined || val === '') return '0';
      const num = typeof val === 'string' ? val.trim() : String(val);
      return num === '' ? '0' : num;
    };

    // Append required fields - these are already validated above
    const emri = safeString(form.emri);
    const kodi = safeString(form.kodi);
    const cmimi_blerjes = safeNumeric(form.cmimi_blerjes);
    const cmimi_shitjes = safeNumeric(form.cmimi_shitjes);
    const kategoria_id = safeString(form.kategoria_id);
    
    // Always append required fields
    data.append('emri', emri);
    data.append('kodi', kodi);
    data.append('cmimi_blerjes', cmimi_blerjes);
    data.append('cmimi_shitjes', cmimi_shitjes);
    data.append('kategoria_id', kategoria_id);
    // Dërgo eshte_aktiv si '1' ose '0' për boolean - Laravel do ta konvertojë
    // Sigurohemi që po dërgohet gjithmonë, edhe nëse është false
    const eshteAktivValue = form.eshte_aktiv === true || form.eshte_aktiv === 'true' || form.eshte_aktiv === 1 || form.eshte_aktiv === '1';
    data.append('eshte_aktiv', eshteAktivValue ? '1' : '0');

    // Append optional fields only if they have values
    if (form.emri_mk && safeString(form.emri_mk) !== '') {
      data.append('emri_mk', safeString(form.emri_mk));
    }
    if (form.pershkrimi && safeString(form.pershkrimi) !== '') {
      data.append('pershkrimi', safeString(form.pershkrimi));
    }
    if (form.pershkrimi_mk && safeString(form.pershkrimi_mk) !== '') {
      data.append('pershkrimi_mk', safeString(form.pershkrimi_mk));
    }
    if (form.zbritje_perqindje !== null && form.zbritje_perqindje !== undefined && form.zbritje_perqindje !== '') {
      data.append('zbritje_perqindje', safeNumeric(form.zbritje_perqindje));
    }
    if (form.furnizues_id && safeString(form.furnizues_id) !== '') {
      data.append('furnizues_id', safeString(form.furnizues_id));
    }

    // Handle new photos
    if (previewReja && previewReja.length > 0) {
      previewReja.forEach((item, index) => {
        if (item.file instanceof File) {
          data.append(`foto[${index}]`, item.file);
          data.append(`ngjyra_foto[${index}]`, item.ngjyra_id || '');
        }
      });
    }

    // Handle existing photos to keep
    if (fotoEkzistuese && fotoEkzistuese.length > 0) {
      const fotoTeMbajtura = fotoEkzistuese.map(f => ({ id: f.id, ngjyra_id: f.ngjyra_id ?? null }));
      data.append('foto_ekzistuese', JSON.stringify(fotoTeMbajtura));
    }

    // Call mutation - errors will be handled by the mutation's onError callback
    // The mutation is async, so we can't use try-catch here
    // Instead, errors are handled in the onError callback of the mutation
    updateProdukt({ id: form.id, data }).catch(error => {
      // This catch prevents uncaught promise rejection
      // The error is already handled by the mutation's onError callback
      console.error('Error caught in handleUpdateProdukt promise:', error);
    });
  };


// const handleFileChange = async (e) => {
//   const files = Array.from(e.target.files);
//   const compressedPhotos = [];

//   for (const file of files) {
//     try {
//       const options = {
//         maxSizeMB: 0.2, // maksimum 300KB
//         maxWidthOrHeight: 1280, // për të ruajtur dimensione të arsyeshme
//         useWebWorker: true,
//       };

//       const compressedFile = await imageCompression(file, options);

//       console.log(`📷 ${file.name} → Para: ${(file.size / 1024).toFixed(1)} KB | Pas: ${(compressedFile.size / 1024).toFixed(1)} KB`);

//       compressedPhotos.push(compressedFile);
//     } catch (error) {
//       console.error(`❌ Gabim gjatë kompresimit të ${file.name}:`, error);
//     }
//   }

//   setForm({ ...form, foto: compressedPhotos });
//   setPreviewPhotos(compressedPhotos.map(file => URL.createObjectURL(file)));
// };


// const handleFileChange = async (e) => {
//   const files = Array.from(e.target.files);
//   const compressedPhotos = [];

//   for (const file of files) {
//     try {
//       const options = {
//         maxSizeMB: 0.2, // maksimum 300KB
//         maxWidthOrHeight: 1280, // për të ruajtur dimensione të arsyeshme
//         useWebWorker: true,
//       };

//       const compressedFile = await imageCompression(file, options);

//       console.log(`📷 ${file.name} → Para: ${(file.size / 1024).toFixed(1)} KB | Pas: ${(compressedFile.size / 1024).toFixed(1)} KB`);

//       compressedPhotos.push(compressedFile);
//     } catch (error) {
//       console.error(`❌ Gabim gjatë kompresimit të ${file.name}:`, error);
//     }
//   }

//   setForm({ 
//   ...form, 
//   foto: compressedPhotos.map(file => ({ file, ngjyra_id: '' })) 
// });
// setPreviewPhotos(compressedPhotos.map(file => URL.createObjectURL(file)));
// };

const handleFileChange = async (e) => {
  const files = Array.from(e.target.files);
  const compressedPhotos = [];

  for (const file of files) {
    try {
      const options = {
        maxSizeMB: 0.2, // maksimum 200KB
        maxWidthOrHeight: 1280,
        useWebWorker: true,
      };

      // Kompreso dhe kthe blob
      const compressedBlob = await imageCompression(file, options);

      // Merr MIME type dhe cakto extension
      const mime = compressedBlob.type; // p.sh. 'image/jpeg'
      const ext = mime === 'image/png' ? 'png' : 'jpg';

      // Merr emrin bazë të file-it pa extension
      const baseName = file.name.split('.').slice(0, -1).join('.') || 'foto';

      // Krijo një emër të ri të sigurt
      const newFileName = `${baseName}_${Date.now()}.${ext}`;

      // Konverto blob në File me emrin dhe MIME-in korrekt
      const compressedFile = new File([compressedBlob], newFileName, {
        type: mime,
        lastModified: Date.now(),
      });

      //console.log(`📷 ${file.name} → Para: ${(file.size / 1024).toFixed(1)} KB | Pas: ${(compressedFile.size / 1024).toFixed(1)} KB | Emër i ri: ${newFileName}`);

      compressedPhotos.push(compressedFile);
    } catch (error) {
      console.error(`❌ Gabim gjatë kompresimit të ${file.name}:`, error);
    }
  }

  // Ruaj në form formatin e duhur me ngjyra
  setForm({
    ...form,
    foto: compressedPhotos.map(file => ({ file, ngjyra_id: '' }))
  });

  // Për foto preview
  setPreviewPhotos(compressedPhotos.map(file => URL.createObjectURL(file)));
};


  const filteredProdukte = produkte
  .filter(p => p.eshte_aktiv != 2) // mos shfaq inaktivët
  .filter((p) =>
    p.emri.toLowerCase().includes(searchTerm) ||
    p.kodi.toLowerCase().includes(searchTerm)
  )
  .filter(p => !kategoriaFilter || p.kategoria_id == kategoriaFilter);

  const produktetFaqes = filteredProdukte.slice((currentPage - 1) * perFaqe, currentPage * perFaqe);
  const numriFaqeve = Math.ceil(filteredProdukte.length / perFaqe);

  const handleDeleteProdukt = async (produkt) => {
    if (!window.confirm("A jeni i sigurt që doni të fshihni këtë produkt?")) return;
    deleteProdukt(produkt.id);
  };



  return (
    <div className="w-full">
      <h1 className="text-3xl md:text-4xl font-bold mb-8 flex items-center gap-3 text-gradient-logo animate-fade-in">
        <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
          <Boxes size={32} className="text-primary-600 dark:text-primary-400" />
        </div>
        Menaxhimi i Produkteve
      </h1>

      {message && (
        <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 font-semibold animate-slide-down ${
          isError 
            ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800' 
            : 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800'
        }`}>
          {isError ? <XCircle size={20} /> : <CheckCircle size={20} />}
          <p>{message}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="card-premium p-6 mb-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <input type="text" placeholder="Emri" value={form.emri} onChange={(e) => setForm({ ...form, emri: e.target.value })} className="input-premium" required />
        <input
  type="text"
  placeholder="Emri (maqedonisht)"
  value={form.emri_mk}
  onChange={(e) => setForm({ ...form, emri_mk: e.target.value })}
  className="input-premium"
/>

        <textarea
          placeholder="Përshkrim (shqip)"
          value={form.pershkrimi}
          onChange={(e) => setForm({ ...form, pershkrimi: e.target.value })}
          className="input-premium col-span-full"
          rows="3"
        />

<textarea
  placeholder="Përshkrim (maqedonisht)"
  value={form.pershkrimi_mk}
  onChange={(e) => setForm({ ...form, pershkrimi_mk: e.target.value })}
  className="input-premium"
/>

        <input type="text" placeholder="Kodi" value={form.kodi} onChange={(e) => setForm({ ...form, kodi: e.target.value })} className="input-premium" required />
        <input type="number" placeholder="Çmimi Blerjes" value={form.cmimi_blerjes} onChange={(e) => setForm({ ...form, cmimi_blerjes: e.target.value })} className="input-premium" required />
        <input type="number" placeholder="Çmimi Shitjes" value={form.cmimi_shitjes} onChange={(e) => setForm({ ...form, cmimi_shitjes: e.target.value })} className="input-premium" required />
        <input type="number" placeholder="Zbritje (%)" value={form.zbritje_perqindje} onChange={(e) => setForm({ ...form, zbritje_perqindje: e.target.value })} className="input-premium" />
        <select value={form.kategoria_id} onChange={(e) => setForm({ ...form, kategoria_id: e.target.value })} className="input-premium" required>
          <option value="">Zgjidh Kategori</option>
          {kategorite.map(k => <option key={k.id} value={k.id}>{k.emri}</option>)}
        </select>
        {settings && settings.furnizuesit_aktiv !== false && (
          <select value={form.furnizues_id} onChange={(e) => setForm({ ...form, furnizues_id: e.target.value })} className="input-premium">
            <option value="">Zgjidh Furnizues</option>
            {furnizuesit.map(f => <option key={f.id} value={f.id}>{f.emri}</option>)}
          </select>
        )}

       {/* <div className="col-span-full flex flex-wrap gap-2">
  {previewPhotos.map((photo, i) => (
    <div key={i} className="relative w-16 h-16 md:w-20 md:h-20">
      <img src={photo} alt="preview" className="w-full h-full object-cover rounded border" />
      <button
        type="button"
        onClick={() => {
          const updated = [...form.foto];
          updated.splice(i, 1);
          setForm({ ...form, foto: updated });
          setPreviewPhotos((prev) => prev.filter((_, index) => index !== i));
        }}
        className="absolute top-0 right-0 bg-red-600 text-white text-xs px-1 rounded-full"
      >
        ✕
      </button>
    </div>
  ))}
</div> */}

{form.foto.length > 0 && previewPhotos.length > 0 && (
  <div className="col-span-full flex flex-wrap gap-2">
    {form.foto.map((f, i) => (
      <div key={i} className="relative w-24">
        <img
          src={previewPhotos[i]}
          alt="preview"
          className="w-full h-20 object-cover rounded border mb-2"
        />

        <select
          value={f.ngjyra_id || ''}
          onChange={(e) => {
            const updated = [...form.foto];
            updated[i].ngjyra_id = e.target.value;
            setForm({ ...form, foto: updated });
          }}
          className="w-full text-sm border rounded"
        >
          <option value="">Pa ngjyrë</option>
          {ngjyrat.map(n => (
            <option key={n.id} value={n.id}>{n.emri}</option>
          ))}
        </select>

        <button
          onClick={() => {
            const updated = [...form.foto];
            updated.splice(i, 1);
            setForm({ ...form, foto: updated });
            setPreviewPhotos(prev => prev.filter((_, index) => index !== i));
          }}
          className="absolute top-0 right-0 text-xs bg-red-500 text-white rounded-full px-1"
        >
          ✕
        </button>
      </div>
    ))}
  </div>
)}



       
        <div className="col-span-full">
          <label className="inline-flex items-center gap-2 bg-secondary-100 dark:bg-secondary-900/30 hover:bg-secondary-200 dark:hover:bg-secondary-900/50 text-secondary-700 dark:text-secondary-300 font-semibold px-4 py-2.5 rounded-xl cursor-pointer shadow-sm transition-all duration-200 hover:scale-105">
            <ImagePlus size={18} /> Ngarko Foto
            <input type="file" multiple onChange={handleFileChange} className="hidden" accept="image/*" />
          </label>
        </div>
        <button
          type="submit"
          className="col-span-full btn-primary flex items-center justify-center gap-2"
          disabled={creatingProdukt}
        >
          <PackagePlus size={20} /> {creatingProdukt ? 'Duke shtuar...' : 'Shto Produkt'}
        </button>

      </form>

      <div className="card-premium p-4 mb-8 flex flex-col md:flex-row gap-4 items-center">
        <div className="relative w-full md:w-1/3">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 dark:text-dark-400" size={18} />
          <input
            type="text"
            placeholder="Kërko..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value.toLowerCase())}
            className="input-premium pl-12"
          />
        </div>
        <div className="relative w-full md:w-1/4">
          <Filter className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 dark:text-dark-400" size={18} />
          <select
            value={kategoriaFilter}
            onChange={(e) => setKategoriaFilter(e.target.value)}
            className="input-premium pl-12"
          >
            <option value="">Të gjitha kategoritë</option>
            {kategorite.map(k => <option key={k.id} value={k.id}>{k.emri}</option>)}
          </select>
        </div>
      </div>

{numriFaqeve > 1 && (
  <div className="card-premium flex items-center justify-center gap-6 mt-8 flex-wrap px-6 py-4">
    <button
      onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
      disabled={currentPage === 1}
      className={`btn-premium-secondary flex items-center gap-2 text-sm ${
        currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''
      }`}
    >
      <ArrowLeft size={16} /> Mbrapa
    </button>

    <span className="text-gray-700 dark:text-gray-300 text-sm font-medium">
      Faqja <span className="text-primary-600 dark:text-primary-400 font-semibold">{currentPage}</span> nga <span className="font-semibold">{numriFaqeve}</span>
    </span>

    <button
      onClick={() => setCurrentPage((prev) => Math.min(prev + 1, numriFaqeve))}
      disabled={currentPage === numriFaqeve}
      className={`btn-premium-secondary flex items-center gap-2 text-sm ${
        currentPage === numriFaqeve ? 'opacity-50 cursor-not-allowed' : ''
      }`}
    >
      Para <ArrowRight size={16} />
    </button>
  </div>
)}



      {loading ? (
        <div className="empty-state">
          <div className="skeleton-card w-full h-64 mb-4"></div>
          <div className="skeleton-card w-full h-64 mb-4"></div>
          <div className="skeleton-card w-full h-64"></div>
        </div>
      ) : produktetFaqes.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <Boxes size={64} className="text-gray-300 dark:text-dark-600" />
          </div>
          <h3 className="empty-state-title">Asnjë produkt nuk u gjet</h3>
          <p className="empty-state-description">Shto produkt të ri për të filluar</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 animate-fade-in">
          {produktetFaqes.map((p) => (
            <ProductCard
              key={p.id}
              produkt={p}
              onDetaje={(prod) => { setSelectedProdukt(prod); setModalType('detaje'); }}
              onEdito={(prod) => handleEdit(prod)}
              onStok={(prod) => { setSelectedProdukt(prod); setModalType('stok'); }}
              onDelete={(prod) => handleDeleteProdukt(prod)}
            />
          ))}
        </div>
      )}

      

      {selectedProdukt && modalType === 'stok' && (
        <StokuModal
          produkt={selectedProdukt}
          onClose={() => { setSelectedProdukt(null); setModalType(null); }}
        />
      )}

      {selectedProdukt && modalType === 'detaje' && (
        <DetajeModal
          produktId={selectedProdukt.id}
          onClose={() => { setSelectedProdukt(null); setModalType(null); }}
        />
      )}

      {selectedProdukt && modalType === 'edit' && (
        <EditModal
          produkt={selectedProdukt}
          onClose={() => { setSelectedProdukt(null); setModalType(null); }}
          onSave={(form, previewReja, fotoEkzistuese) => {
            handleUpdateProdukt({ ...form, id: selectedProdukt.id }, previewReja, fotoEkzistuese);
          }}
        />
      )}
    </div>
  );
}

export default Produkte;
